import React, { useEffect, useState } from 'react';
import EmployeeDetails from '../../components/EmployeeDetails/EmployeeDetails';
import EmployeeTable from '../../components/employeeTable/EmployeeTable';
import EmployeeSearchBar from '../../components/EmployeeSearchBar';
import EmployeeAdvancedSearchModal from '../../components/EmployeeAdvancedSearchModal';
import { getAuthHeaders } from '../../utils/auth';
import axios from 'axios';
import {
  fetchAttendanceById,
  fetchEmployeeById,
  isAttendanceNotificationType,
  parseNotificationActionError,
} from '../../utils/notificationNavigation';

export default function EmployeePage({
  employees,
  userProfile,
  handleEditEmployee,
  formatCurrency,
  getRoleDisplayName,
  getRoleColor,
  getGroupColor,
  getStatusColor,
  getPageTitle,
  setShowAddForm,
  page,
  limit,
  total,
  setPage,
  setLimit,
  search,
  setSearch,
  sortBy,
  setSortBy,
  onSearchChange,
  sortOrder,
  setSortOrder,
  loading,
  onAdvancedSearch,  
  advancedSearchValues,
  currentView,
  pendingNotificationAction,
  onNotificationActionHandled,
  onExportEmployees,
  exporting,
  exportError,
  onExportMissingCheckinFace,
  exportingMissingFace,
  missingFaceExportError,
  canExportEmployees,
  canDeleteEmployee,
  onDeleteEmployee,
  onRowStatusUpdate,
}) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openAttendanceRecord, setOpenAttendanceRecord] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFields, setAdvFields] = useState({
    status: '',
    employee_no: '',
    name: ''
  });
  const [profile , setProfile] = useState([]);

  // Keep modal fields in sync with parent if needed
  React.useEffect(() => {
    if (advancedSearchValues) setAdvFields(advancedSearchValues);
  }, [advancedSearchValues]);

  const handleRowClick = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleAdvChange = (e) => {
    const { name, value } = e.target;
    setAdvFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdvSubmit = (e) => {
    e.preventDefault();
    setShowAdvanced(false);
    if (onAdvancedSearch) onAdvancedSearch(advFields);
  };

  const handleAdvReset = () => {
    setAdvFields({ status: '', employee_no: '', name: '' });
    if (onAdvancedSearch) onAdvancedSearch({});
    setShowAdvanced(false);
  };

  const fetchProfile = async () => {
  
    try {
      const headers = await getAuthHeaders();
     
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employees/me`, {
        headers,
     
      });
      setProfile(response.data.employees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      // Handle authentication errors
      if (error.message && error.message.includes('sign in')) {
        alert('Authentication required. Please sign in with Microsoft.');
        window.location.reload();
      }
    } finally {
       
    }
  };


    useEffect(() => {
    const handler = setTimeout(() => {
    if (userProfile?.role === 'staff' ) { 
      fetchProfile();
    }
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (
      !pendingNotificationAction
      || !isAttendanceNotificationType(pendingNotificationAction.type)
    ) {
      return undefined;
    }

    let cancelled = false;

    const openFromNotification = async () => {
      try {
        const headers = await getAuthHeaders();
        const record = await fetchAttendanceById(
          pendingNotificationAction.entityId,
          headers
        );
        const employee = await fetchEmployeeById(record.employee_id, headers);
        if (cancelled) return;
        setSelectedEmployee(employee);
        setOpenAttendanceRecord(record);
      } catch (error) {
        if (!cancelled) {
          alert(parseNotificationActionError(error));
          onNotificationActionHandled?.();
        }
      }
    };

    openFromNotification();

    return () => {
      cancelled = true;
    };
  }, [pendingNotificationAction, onNotificationActionHandled]);

  const handleAttendanceNotificationHandled = () => {
    setOpenAttendanceRecord(null);
    onNotificationActionHandled?.();
  };

  const handleDeleteEmployeeClick = (employee) => {
    if (!onDeleteEmployee) return;
    onDeleteEmployee(employee, { onSuccess: () => setSelectedEmployee(null) });
  };

  return (
    <div className="w-full min-w-0">
      {!selectedEmployee && userProfile?.role !== 'staff' && (
        <div className="section-header flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 min-w-0 flex-1">
            <h2>{getPageTitle()}</h2>
            <EmployeeSearchBar onSearchChange={onSearchChange} search={search} setSearch={setSearch} onShowAdvanced={() => setShowAdvanced(true)} />
          </div>
          {canExportEmployees && (
            <div className="flex flex-wrap gap-2 shrink-0 self-start sm:self-center">
              <button
                type="button"
                onClick={onExportEmployees}
                disabled={exporting || exportingMissingFace || loading}
                className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? 'Exporting…' : 'Export to Excel'}
              </button>
              <button
                type="button"
                onClick={onExportMissingCheckinFace}
                disabled={exportingMissingFace || exporting || loading}
                className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingMissingFace ? 'Exporting…' : 'Export Missing Face Image'}
              </button>
            </div>
          )}

          <EmployeeAdvancedSearchModal
            open={showAdvanced}
            onClose={() => setShowAdvanced(false)}
            advFields={advFields}
            onChange={handleAdvChange}
            onSubmit={handleAdvSubmit}
            onReset={handleAdvReset}
          />
        </div>
      )}

      {exportError && !selectedEmployee && userProfile?.role !== 'staff' && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
          {exportError}
        </div>
      )}

      {missingFaceExportError && !selectedEmployee && userProfile?.role !== 'staff' && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
          {missingFaceExportError}
        </div>
      )}

      {selectedEmployee || userProfile?.role === 'staff' ? (
        <div>
          <div>
            <EmployeeDetails
              employee={userProfile?.role === 'staff' ? profile[0] : selectedEmployee}
              onBack={() => setSelectedEmployee(null)}
              userProfile={userProfile}
              formatCurrency={formatCurrency}
              getRoleDisplayName={getRoleDisplayName}
              getRoleColor={getRoleColor}
              getGroupColor={getGroupColor}
              getStatusColor={getStatusColor}
              handleEditEmployee={handleEditEmployee}
              currentView={currentView}
              openAttendanceRecord={openAttendanceRecord}
              onAttendanceNotificationHandled={handleAttendanceNotificationHandled}
              canDeleteEmployee={canDeleteEmployee}
              onDeleteEmployee={handleDeleteEmployeeClick}
              onEmployeeUpdated={(updatedEmployee) => {
                setSelectedEmployee(updatedEmployee);
              }}
            />
          </div>
        </div>
      ) :
        <EmployeeTable
          employees={employees}
          userProfile={userProfile}
          handleEditEmployee={handleEditEmployee}
          onRowClick={handleRowClick}
          page={page}
          limit={limit}
          total={total}
          setPage={setPage}
          setLimit={setLimit}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          loading={loading}
          onRowStatusUpdate={onRowStatusUpdate}
        />}
    </div>
  );
}